import { useState, createContext, useContext, useEffect, useCallback } from "react";
import { mockTenantDefault, mockTenantNavy, mockTenantPastel, dataTest } from "./mockData";
import { parseApiResponse } from "../utils/strParsing";
import { translations } from "../i18n/translations" ;

import API_CONFIG from '../config/api'
const BASE_URL = API_CONFIG.BASE_URL

const AppContext = createContext();

export const useApp = () => {
	const ctx = useContext(AppContext);
	if (!ctx) throw new Error("useApp must be used within AppProvider");
	return ctx;
};

export const AppProvider = ({ children }) => {
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState(null);
	const [tenantInfos, setTenantInfos] = useState(undefined)
	const [theme, setTheme] = useState(undefined);
	const [features, setFeatures] = useState(undefined);
	const [language, setLanguage] = useState(() => {
		return localStorage.getItem('appLanguage') || 'en';
	});

	const changeLanguage = (langCode) => {
		setLanguage(langCode);
		localStorage.setItem('appLanguage', langCode);
	};

	const t = (key) => {
		return translations[language]?.[key] || translations['en'][key] || key;
	};

	const applyTheme = (themeObj) => {
		for (const [key, value] of Object.entries(themeObj)) {
		  document.documentElement.style.setProperty(`--${key}`, value);
		}
	};




	const fetchAppInit = useCallback(async () => {
		setIsLoading(true);
		setError(null);

		try {
			const client_url = new URL(window.location.href);
			const [tenant_id, access_token] = client_url.pathname.split("/").filter(Boolean);
			const api_url = BASE_URL + `/cui_portal/${tenant_id}/${access_token}/init`
			const result = await fetch(api_url); 
			if (result.status === 404) {
				// set error to the proper code and detailed message
				setError({
					statusCode: 404,
					detail: t("error404")
				})
				return;
			}

			if (result.status === 401) {
				setError({
					statusCode: 401,
					detail: t("error401")
				})
				return;
			}

			if (!result.ok) {
				setError({
					statusCode: 500,
					detail: t("error500")
				})
				return
			}

			let datas = parseApiResponse(await result.json());
			if (datas) {
				// set the theme, call applyTheme, the features config and the appInfos
				// set error to null
				// set isLoading to false
				setFeatures(datas.features);
				setTenantInfos(datas.tenant);
				if (datas.theme)
					applyTheme(datas.theme)
				setTheme(datas.theme);
				setError(null);
				setIsLoading(false);
				return

			} else {
				setError({
					statusCode: 422,
					detail: t("error422")
				});
				setIsLoading(false);
			}

		} catch (err) {
			// set error to the proper code and detailed message
			setError({
				statusCode: 422,
				detail: t("error422")
			});
			setIsLoading(false);
		} finally {
			setIsLoading(false);
		}
	}, []);

	useEffect(() => {
		fetchAppInit();
	}, [fetchAppInit]);

	return (
		<AppContext.Provider
		  value={{
			isLoading,
			error,
			tenantInfos,
			theme,
			features,
			reloadAppConfig: fetchAppInit,
			language,
			changeLanguage,
			t,
		  }}
		>
		  {children}
		</AppContext.Provider>
	  );


};